// The IndexedDB half of the payload store (`payloadStore.ts`): database `storydress-projects`.
//
// **Its own database, not a new store in `storydress-media`.** Adding an object store means
// bumping that database's version, and every page still running the previous build opens it at
// version 1: after the bump that open fails with `VersionError`, so a tab left open across a
// deploy would lose its media reads and writes until reloaded — and the upgrade itself is blocked
// while such a tab holds a connection. A database of its own has neither problem. Same reasoning
// as `render/renderFolder.ts` and `render/pendingExport.ts`.
//
//   payloads    key: `storydress:project:<id>:<slot>`   value: { v: <payload string, verbatim>, at }
//   superseded  key: `<payload key>@<at>`                value: { key, v, at, archivedAt, reason }
//
// The payload is stored as the exact string localStorage held — not parsed — so a backup still
// copies it byte for byte (README, "The payloads travel verbatim").

import type { PayloadBackend } from './payloadStore';
import { supersededToPrune, type StoredPayload } from './payloadMigration';

export const PAYLOAD_DB_NAME = 'storydress-projects';
const DB_VERSION = 1;
const PAYLOADS = 'payloads';
const SUPERSEDED = 'superseded';

function txError(tx: IDBTransaction, fallback: string): unknown {
  return tx.error ?? new DOMException(fallback, 'AbortError');
}

function isRecord(value: unknown): value is StoredPayload {
  const r = value as StoredPayload | null;
  return Boolean(r && typeof r.v === 'string' && typeof r.at === 'number');
}

export function idbPayloadBackend(factory: IDBFactory = indexedDB): PayloadBackend {
  // One connection for the life of the page: autosave writes every few hundred milliseconds, and
  // opening a database per write is the slow part. Dropped if another tab ever upgrades it.
  let opening: Promise<IDBDatabase> | null = null;
  const db = (): Promise<IDBDatabase> => {
    if (opening) return opening;
    opening = new Promise<IDBDatabase>((resolve, reject) => {
      const req = factory.open(PAYLOAD_DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const d = req.result;
        if (!d.objectStoreNames.contains(PAYLOADS)) d.createObjectStore(PAYLOADS);
        if (!d.objectStoreNames.contains(SUPERSEDED)) d.createObjectStore(SUPERSEDED);
      };
      req.onsuccess = () => {
        const d = req.result;
        d.onversionchange = () => {
          d.close();
          opening = null;
        };
        d.onclose = () => {
          opening = null;
        };
        resolve(d);
      };
      req.onerror = () => {
        opening = null;
        reject(req.error);
      };
      req.onblocked = () => console.warn('[payloads] opening the project database is waiting for another tab');
    });
    return opening;
  };

  const commit = (tx: IDBTransaction) => {
    // Ask for the commit now rather than when the event loop gets round to it — the shorter the
    // window between "written" and "on disk", the less a closing tab can lose.
    try {
      (tx as IDBTransaction & { commit?: () => void }).commit?.();
    } catch {
      /* already committing */
    }
  };

  return {
    async readAll() {
      const d = await db();
      return new Promise<Map<string, StoredPayload>>((resolve, reject) => {
        const out = new Map<string, StoredPayload>();
        const tx = d.transaction(PAYLOADS, 'readonly');
        const req = tx.objectStore(PAYLOADS).openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) return;
          // One bad record costs only itself (rule 5): it is skipped and named, not fatal.
          try {
            const value = cursor.value as unknown;
            if (isRecord(value)) out.set(String(cursor.key), value);
            else console.warn(`[payloads] record ${String(cursor.key)} has an unknown shape; skipped`);
          } catch (err) {
            console.warn(`[payloads] record ${String(cursor.key)} is unreadable`, err);
          }
          cursor.continue();
        };
        tx.oncomplete = () => resolve(out);
        tx.onerror = () => reject(txError(tx, 'read failed'));
        tx.onabort = () => reject(txError(tx, 'read aborted'));
      });
    },

    async read(key) {
      const d = await db();
      return new Promise<StoredPayload | null>((resolve, reject) => {
        const tx = d.transaction(PAYLOADS, 'readonly');
        const req = tx.objectStore(PAYLOADS).get(key);
        tx.oncomplete = () => resolve(isRecord(req.result) ? req.result : null);
        tx.onerror = () => reject(txError(tx, 'read failed'));
        tx.onabort = () => reject(txError(tx, 'read aborted'));
      });
    },

    async write(puts, deletes) {
      if (puts.length === 0 && deletes.length === 0) return;
      const d = await db();
      await new Promise<void>((resolve, reject) => {
        const tx = d.transaction(PAYLOADS, 'readwrite');
        const store = tx.objectStore(PAYLOADS);
        for (const [key, record] of puts) store.put(record, key);
        for (const key of deletes) store.delete(key);
        tx.oncomplete = () => resolve();
        // A full disk surfaces here as `QuotaExceededError` — on the transaction, not the request.
        tx.onerror = () => reject(txError(tx, 'write failed'));
        tx.onabort = () => reject(txError(tx, 'write aborted'));
        commit(tx);
      });
    },

    async archive(key, record, reason) {
      const d = await db();
      await new Promise<void>((resolve, reject) => {
        const tx = d.transaction(SUPERSEDED, 'readwrite');
        const store = tx.objectStore(SUPERSEDED);
        store.put({ key, v: record.v, at: record.at, archivedAt: Date.now(), reason }, `${key}@${record.at}`);
        const keysReq = store.getAllKeys();
        keysReq.onsuccess = () => {
          for (const id of supersededToPrune((keysReq.result as IDBValidKey[]).map(String), key)) store.delete(id);
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(txError(tx, 'archive failed'));
        tx.onabort = () => reject(txError(tx, 'archive aborted'));
      });
    },
  };
}
